import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import InvoiceColumns from 'src/assets/static/receipt_details/InvoiceColumns';
import GridWrapper from 'src/components/GridWrapper';
import {
  checkIfLinkedToAnotherReceipt,
  fetchInvoiceData,
  linkInvoiceToReceipt,
  selectInvoiceUse,
} from 'src/features/InvoiceGridSlice';
import useCompany from 'src/hooks/useCompany';
import { InvoiceItems } from 'src/types/Invoices';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { transformResponse } from 'src/utils/transformation';

import { SortDescriptor } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridDataStateChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { api } from 'src/assets/static/ApiRoutes';
import LoadingPanel from 'src/components/LoadingPanel';
import Button from 'src/components/design-system/Button';
import axios from 'src/utils/axios';
import ConfirmLinkModal from './ConfirmLinkModal';

interface ManageInvoiceLinkProps {
  showModal: (modalName: string) => void;
}
interface Response {
  items: InvoiceItems[];
  totalRecords: number;
}

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const ManageInvoiceLinks: React.FC<ManageInvoiceLinkProps> = ({ showModal }) => {
  const INVOICE_DATA_ITEM: string = 'receiptId';
  const [searchParams] = useSearchParams();
  const initialInvoiceDataState = {
    skip: 0,
    take: 20,
    skipCalculatingTotalRecordsCount: false,
    sort: [
      {
        field: 'receiptId',
        dir: 'asc',
      },
    ],
    filters: [{ field: 'poId', value: searchParams.get('poid'), operator: 'eq' }],
  };
  const initialSort: Array<SortDescriptor> = [{ field: 'invoiceNumber', dir: 'asc' }];
  const dispatch = useAppDispatch();
  const { company: selectedCompany } = useCompany();
  const { invoiceItems, invoiceLoading } = useAppSelector(selectInvoiceUse);
  const [invoiceDataState, setInvoiceDataState] = useState<any>(initialInvoiceDataState);
  const [invoiceProcessData, setInvoiceProcessData] = useState<InvoiceItems[]>(invoiceItems.data);
  const [openConfirmLinkModal, setOpenConfirmLinkModal] = useState(false);
  const [, setSort] = useState(initialSort);
  const [prevReceiptId, setPrevReceiptId] = useState<string | undefined>();
  const [selectedInvoiceRow, setSelectedInvoiceRow] = useState<InvoiceItems>({} as InvoiceItems);
  const [count, setCount] = useState(0);
  const listInvoices = useCallback(
    (companyId: string) =>
      axios.post<Response>(api.getInvoiceDataUrlFn(companyId), invoiceDataState).then((res) => {
        setInvoiceProcessData(transformResponse(res.data.items, 'LINK_INVOICES'));
        setCount(res.data.totalRecords);
      }),
    [invoiceDataState]
  );
  useEffect(() => {
    listInvoices(selectedCompany.companyId);
  }, [selectedCompany, listInvoices]);

  const onInvoiceDataStateChange = (e: GridDataStateChangeEvent) => {
    dispatch(
      fetchInvoiceData({ companyId: selectedCompany.companyId, body: modifyRequest(e.dataState) })
    );
    setInvoiceDataState(e.dataState);
  };
  //================================================Selection======================================
  const onSelectionInvoiceChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setInvoiceProcessData((prev) => {
        const previousItems = [...prev];
        const index = event.startRowIndex;
        previousItems[index].selected = !previousItems[index].selected;
        return previousItems;
      });
      setSelectedInvoiceRow(
        invoiceProcessData?.filter((invoice: InvoiceItems) => invoice.selected === true)[0]
      );
    },
    [invoiceProcessData, setSelectedInvoiceRow]
  );

  // selection row logic

  //=====================================================Selection=====================================

  const onLinkInvoiceClick = () => {
    const body = {
      skip: 0,
      take: 1,
      sort: [],
      filters: [
        {
          field: 'recvReceiptReceiptId',
          value: selectedInvoiceRow?.receiptId.toString(),
          operator: 'eq',
        },
      ],
    };
    if (selectedInvoiceRow) {
      dispatch(checkIfLinkedToAnotherReceipt({ companyId: selectedCompany?.companyId, body })).then(
        (res: any) => {
          if (res?.payload?.items?.length === 0) {
            dispatch(
              linkInvoiceToReceipt({
                receiptId: searchParams.get('recid'),
                invoiceId: selectedInvoiceRow?.receiptId,
              })
            )
              .unwrap()
              .then(() => {
                showModal(`Link a document to Receipt ${searchParams.get('recid')}`);
              })
              .catch(console.error);
          } else {
            setPrevReceiptId(res?.payload?.items[0]?.receivingReceiptId);
            setOpenConfirmLinkModal(true);
          }
        }
      );
    }
  };
  return (
    <>
      <div>
        <div style={{ marginBottom: '50px' }}>
          <div style={{ lineHeight: '1.5', marginBottom: '20px' }}>
            <b>Choose a document to link to this Receipt.</b>
          </div>
          <div>
            {invoiceLoading === 'pending' ? <LoadingPanel /> : ''}
            <ConfirmLinkModal
              id="completeModal"
              visible={openConfirmLinkModal}
              width="400px"
              closeModal={() => setOpenConfirmLinkModal(false)}
              closeParentModal={showModal}
              receiptId={searchParams.get('recid')}
              title="Link Selected Invoice"
              height="300px"
              invoiceId={selectedInvoiceRow?.receiptId || ''}
              invoiceNumber={selectedInvoiceRow?.invoiceNumber}
              previouslyLinkedReceiptId={prevReceiptId}
            />
            <GridWrapper
              columns={InvoiceColumns}
              dataSource={transformResponse(invoiceProcessData, 'LINK_INVOICES')}
              dataState={invoiceDataState}
              sortable
              resizable
              height="50vh"
              total={count}
              showColumnMenu
              DataItemKey={INVOICE_DATA_ITEM}
              selectable={selectable}
              onDataStateChange={onInvoiceDataStateChange}
              pageable={gridPagerSettings}
              onSelectionChange={onSelectionInvoiceChange}
              onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
              tableTextId="invoices"
            />
          </div>
        </div>
        <DialogActionsBar>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              color="info"
              disabled={
                invoiceProcessData?.filter((invoice: InvoiceItems) => invoice.selected === true)
                  .length !== 1
              }
              onClick={onLinkInvoiceClick}
            >
              Link selected Document
            </Button>
            <Button color="info" onClick={() => showModal('Receipt History')}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </div>
    </>
  );
};

export default ManageInvoiceLinks;
